import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../config';
import { 
  Chart as ChartJS, 
  ArcElement, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend,
  TimeScale
} from 'chart.js';
import { Pie, Bar, Line } from 'react-chartjs-2';
import 'chartjs-adapter-date-fns';
import './AnalyticsDashboard.css';

// Register ChartJS components
ChartJS.register(
  ArcElement, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend,
  TimeScale
);

function AnalyticsDashboard() {
  const [loading, setLoading] = useState(true);
  const [staffData, setStaffData] = useState([]);
  const [checkHistory, setCheckHistory] = useState([]);
  const [timeRange, setTimeRange] = useState('month'); // 'week', 'month', 'year'
  const [businessId] = useState(() => localStorage.getItem('businessId'));
  const [environment] = useState(() => localStorage.getItem('userEnvironment') || 'test');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch staff data with detailed license information
        const staffResponse = await axiosInstance.get('/api/staff', {
          params: { includeDetails: true }
        });
        console.log('Staff data response:', staffResponse.data);
        setStaffData(staffResponse.data || []);
        
        // Fetch check history from dvla_checks table instead of license_checks
        const historyResponse = await axiosInstance.get('/api/staff/check-history');
        console.log('Check history response:', historyResponse.data);
        
        // Transform the data to ensure check_date is properly formatted
        const formattedHistory = historyResponse.data.map(check => ({
          ...check,
          // Ensure check_date is in ISO format for consistent parsing
          check_date: check.check_date ? new Date(check.check_date).toISOString() : null
        }));
        
        setCheckHistory(formattedHistory || []);
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [businessId, environment]);

  // Prepare data for license status distribution chart
  const prepareStatusData = () => {
    const statusCounts = {
      'VALID': 0,
      'EXPIRED': 0,
      'SUSPENDED': 0,
      'REVOKED': 0,
      'DISQUALIFIED': 0,
      'SURRENDERED': 0,
      'UNKNOWN': 0
    };

    staffData.forEach(staff => {
      const status = staff.status?.toUpperCase() || 'UNKNOWN';
      if (statusCounts.hasOwnProperty(status)) {
        statusCounts[status]++;
      } else {
        statusCounts['UNKNOWN']++;
      }
    });

    return {
      labels: Object.keys(statusCounts),
      datasets: [
        {
          data: Object.values(statusCounts),
          backgroundColor: [
            '#4CAF50', // VALID - Green
            '#FF9800', // EXPIRED - Orange
            '#F44336', // SUSPENDED - Red
            '#E91E63', // REVOKED - Pink
            '#9C27B0', // DISQUALIFIED - Purple
            '#795548', // SURRENDERED - Brown
            '#9E9E9E'  // UNKNOWN - Gray
          ],
          borderWidth: 1
        }
      ]
    };
  };

  // Prepare data for points distribution chart
  const preparePointsData = () => {
    const pointsRanges = {
      '0 points': 0,
      '1-3 points': 0,
      '4-6 points': 0,
      '7-9 points': 0,
      '10-12 points': 0,
      '12+ points': 0
    };

    staffData.forEach(staff => {
      const points = parseInt(staff.points_total) || 0;
      
      if (points === 0) pointsRanges['0 points']++;
      else if (points <= 3) pointsRanges['1-3 points']++;
      else if (points <= 6) pointsRanges['4-6 points']++;
      else if (points <= 9) pointsRanges['7-9 points']++;
      else if (points <= 12) pointsRanges['10-12 points']++;
      else pointsRanges['12+ points']++;
    });

    return {
      labels: Object.keys(pointsRanges),
      datasets: [
        {
          label: 'Number of Drivers',
          data: Object.values(pointsRanges),
          backgroundColor: [
            '#4CAF50', // 0 points - Green
            '#8BC34A', // 1-3 points - Light Green
            '#FFEB3B', // 4-6 points - Yellow
            '#FF9800', // 7-9 points - Orange
            '#FF5722', // 10-12 points - Deep Orange
            '#F44336'  // 12+ points - Red
          ],
          borderWidth: 1
        }
      ]
    };
  };

  // Prepare data for license expiry timeline
  const prepareExpiryData = () => {
    // Get next 12 months
    const months = [];
    const counts = [];
    const now = new Date();
    
    for (let i = 0; i < 12; i++) {
      const month = new Date(now.getFullYear(), now.getMonth() + i, 1);
      const monthName = month.toLocaleString('default', { month: 'short', year: '2-digit' });
      months.push(monthName);
      counts.push(0);
    }

    console.log('Staff data for expiry analysis:', staffData.map(s => ({
      id: s.id,
      name: `${s.first_name} ${s.last_name}`,
      license_valid_to: s.license_valid_to,
      common_entitlement_expiry: s.common_entitlement_expiry
    })));

    // Count licenses expiring in each month
    staffData.forEach(staff => {
      // Check both license_valid_to and common_entitlement_expiry fields
      let expiryDateStr = staff.license_valid_to || staff.common_entitlement_expiry;
      
      if (expiryDateStr) {
        console.log(`Processing expiry date for ${staff.first_name} ${staff.last_name}: ${expiryDateStr}`);
        
        // Convert to Date object and validate
        const expiryDate = new Date(expiryDateStr);
        
        if (!isNaN(expiryDate.getTime())) {
          const monthDiff = (expiryDate.getFullYear() - now.getFullYear()) * 12 + 
                          (expiryDate.getMonth() - now.getMonth());
          
          console.log(`Month diff: ${monthDiff} for date: ${expiryDate.toISOString()}`);
          
          if (monthDiff >= 0 && monthDiff < 12) {
            counts[monthDiff]++;
          }
        } else {
          console.log(`Invalid date: ${expiryDateStr}`);
        }
      }
    });

    console.log('License expiry data:', { months, counts });
    
    return {
      labels: months,
      datasets: [
        {
          label: 'Licenses Expiring',
          data: counts,
          borderColor: '#2196F3',
          backgroundColor: 'rgba(33, 150, 243, 0.2)',
          fill: true,
          tension: 0.4
        }
      ]
    };
  };

  // Prepare data for check activity over time
  const prepareCheckActivityData = () => {
    // Determine date range based on selected time range
    const now = new Date();
    let startDate;
    let dateFormat;
    let groupBy;
    
    switch(timeRange) {
      case 'week':
        startDate = new Date(now);
        startDate.setDate(now.getDate() - 7);
        dateFormat = { weekday: 'short' };
        groupBy = 'day';
        break;
      case 'year':
        startDate = new Date(now);
        startDate.setFullYear(now.getFullYear() - 1);
        dateFormat = { month: 'short' };
        groupBy = 'month';
        break;
      case 'month':
      default:
        startDate = new Date(now);
        startDate.setMonth(startDate.getMonth() - 1);
        dateFormat = { day: 'numeric', month: 'short' };
        groupBy = 'day';
    }

    console.log('Check activity date range:', { 
      startDate: startDate.toISOString(), 
      endDate: now.toISOString(),
      checkHistoryCount: checkHistory.length 
    });

    // Log a sample of check history data to debug format issues
    if (checkHistory.length > 0) {
      console.log('Sample check history item:', checkHistory[0]);
    }

    // Filter checks within the date range
    const filteredChecks = checkHistory.filter(check => {
      if (!check.check_date) {
        console.log('Check missing check_date:', check);
        return false;
      }
      
      const checkDate = new Date(check.check_date);
      
      if (isNaN(checkDate.getTime())) {
        console.log('Invalid check date:', check.check_date);
        return false;
      }
      
      const isInRange = checkDate >= startDate && checkDate <= now;
      return isInRange;
    });

    console.log('Filtered checks count:', filteredChecks.length);
    
    if (filteredChecks.length > 0) {
      console.log('First filtered check:', filteredChecks[0]);
    }

    // Group checks by date
    const checksByDate = {};
    
    if (groupBy === 'day') {
      // Initialize all days in the range
      for (let d = new Date(startDate); d <= now; d.setDate(d.getDate() + 1)) {
        const dateKey = d.toISOString().split('T')[0];
        checksByDate[dateKey] = 0;
      }
    } else if (groupBy === 'month') {
      // Initialize all months in the range
      for (let m = new Date(startDate); m <= now; m.setMonth(m.getMonth() + 1)) {
        const dateKey = `${m.getFullYear()}-${m.getMonth() + 1}`;
        checksByDate[dateKey] = 0;
      }
    }

    // Count checks for each date
    filteredChecks.forEach(check => {
      if (!check.check_date) return;
      
      const checkDate = new Date(check.check_date);
      let dateKey;
      
      if (groupBy === 'day') {
        dateKey = checkDate.toISOString().split('T')[0];
      } else if (groupBy === 'month') {
        dateKey = `${checkDate.getFullYear()}-${checkDate.getMonth() + 1}`;
      }
      
      if (dateKey in checksByDate) {
        checksByDate[dateKey]++;
      } else {
        console.log(`Date key not found in initialized range: ${dateKey}`);
        checksByDate[dateKey] = 1;
      }
    });

    // Format labels based on date format
    const labels = Object.keys(checksByDate).map(dateKey => {
      if (groupBy === 'day') {
        return new Date(dateKey).toLocaleDateString('en-GB', dateFormat);
      } else if (groupBy === 'month') {
        const [year, month] = dateKey.split('-');
        return new Date(parseInt(year), parseInt(month) - 1, 1).toLocaleDateString('en-GB', dateFormat);
      }
      return dateKey;
    });

    console.log('Check activity data:', { 
      labels, 
      data: Object.values(checksByDate),
      dateKeys: Object.keys(checksByDate)
    });

    return {
      labels,
      datasets: [
        {
          label: 'License Checks',
          data: Object.values(checksByDate),
          borderColor: '#673AB7',
          backgroundColor: 'rgba(103, 58, 183, 0.2)',
          fill: true,
          tension: 0.1
        }
      ]
    };
  };

  // Calculate compliance metrics
  const calculateComplianceMetrics = () => {
    const totalStaff = staffData.length;
    if (totalStaff === 0) return { percentage: 0, valid: 0, total: 0 };
    
    const validLicenses = staffData.filter(staff => 
      staff.status?.toUpperCase() === 'VALID'
    ).length;
    
    return {
      percentage: Math.round((validLicenses / totalStaff) * 100),
      valid: validLicenses,
      total: totalStaff
    };
  };

  // Calculate risk metrics
  const calculateRiskMetrics = () => {
    const highRisk = staffData.filter(staff => {
      const points = parseInt(staff.points_total) || 0;
      const status = staff.status?.toUpperCase();
      return points >= 7 || ['SUSPENDED', 'REVOKED', 'DISQUALIFIED'].includes(status);
    }).length;
    
    const mediumRisk = staffData.filter(staff => {
      const points = parseInt(staff.points_total) || 0;
      const status = staff.status?.toUpperCase();
      return (points >= 4 && points < 7) || status === 'EXPIRED';
    }).length;
    
    const lowRisk = staffData.length - highRisk - mediumRisk;
    
    return { highRisk, mediumRisk, lowRisk };
  };

  // Calculate check metrics
  const calculateCheckMetrics = () => {
    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    
    console.log('Calculating check metrics from', checkHistory.length, 'checks');
    
    const checksLastMonth = checkHistory.filter(check => {
      if (!check.check_date) return false;
      
      const checkDate = new Date(check.check_date);
      if (isNaN(checkDate.getTime())) {
        console.log('Invalid check date in metrics calculation:', check.check_date);
        return false;
      }
      
      return checkDate >= lastMonth;
    }).length;
    
    console.log('Checks in last month:', checksLastMonth);
    
    return {
      total: checkHistory.length,
      lastMonth: checksLastMonth
    };
  };

  // Add this new function to prepare age distribution data
  const prepareAgeDistributionData = () => {
    const ageCategories = {
      '21-24': 0,
      '25-29': 0,
      '30+': 0
    };
    
    staffData.forEach(staff => {
      const age = calculateAge(staff.date_of_birth);
      if (isNaN(age)) return;
      
      if (age >= 21 && age < 25) {
        ageCategories['21-24']++;
      } else if (age >= 25 && age < 30) {
        ageCategories['25-29']++;
      } else if (age >= 30) {
        ageCategories['30+']++;
      }
    });
    
    const total = Object.values(ageCategories).reduce((sum, count) => sum + count, 0);
    
    return {
      labels: Object.keys(ageCategories),
      datasets: [
        {
          label: 'Drivers by Age Group',
          data: Object.values(ageCategories),
          backgroundColor: [
            'rgba(54, 162, 235, 0.7)',
            'rgba(75, 192, 192, 0.7)',
            'rgba(153, 102, 255, 0.7)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(153, 102, 255, 1)'
          ],
          borderWidth: 1
        }
      ],
      total: total,
      percentages: Object.entries(ageCategories).reduce((acc, [key, value]) => {
        acc[key] = total > 0 ? Math.round((value / total) * 100) : 0;
        return acc;
      }, {})
    };
  };

  // Helper function to calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return NaN;
    
    const birthDate = new Date(dateOfBirth);
    if (isNaN(birthDate.getTime())) return NaN;
    
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDifference = today.getMonth() - birthDate.getMonth();
    
    if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  if (loading) {
    return <div className="analytics-loading">Loading analytics data...</div>;
  }

  const complianceMetrics = calculateComplianceMetrics();
  const riskMetrics = calculateRiskMetrics();
  const checkMetrics = calculateCheckMetrics();

  return (
    <div className="analytics-dashboard">
      <h2>Driver License Analytics Dashboard</h2>
      
      {/* Summary Metrics */}
      <div className="metrics-summary">
        <div className="metric-card compliance">
          <h3>Compliance Rate</h3>
          <div className="metric-value">{complianceMetrics.percentage}%</div>
          <div className="metric-detail">
            {complianceMetrics.valid} of {complianceMetrics.total} drivers compliant
          </div>
        </div>
        
        <div className="metric-card risk">
          <h3>Risk Assessment</h3>
          <div className="risk-indicators">
            <div className="risk-indicator high">
              <span className="risk-label">High</span>
              <span className="risk-count">{riskMetrics.highRisk}</span>
              <div className="risk-tooltip">
                Drivers with 7+ penalty points, or with license status: Suspended, Revoked, or Disqualified
              </div>
            </div>
            <div className="risk-indicator medium">
              <span className="risk-label">Medium</span>
              <span className="risk-count">{riskMetrics.mediumRisk}</span>
              <div className="risk-tooltip">
                Drivers with 4-6 penalty points, or with license status: Expired
              </div>
            </div>
            <div className="risk-indicator low">
              <span className="risk-label">Low</span>
              <span className="risk-count">{riskMetrics.lowRisk}</span>
              <div className="risk-tooltip">
                Drivers with 0-3 penalty points and a valid license status
              </div>
            </div>
          </div>
        </div>
        
        <div className="metric-card checks">
          <h3>License Checks</h3>
          <div className="metric-value">{checkMetrics.total}</div>
          <div className="metric-detail">
            {checkMetrics.lastMonth} in the last 30 days
          </div>
        </div>
      </div>
      
      {/* Charts Section */}
      <div className="charts-container">
        <div className="chart-row">
          <div className="chart-card">
            <h3>License Status Distribution</h3>
            <div className="chart-container">
              <Pie 
                data={prepareStatusData()} 
                options={{
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.label || '';
                          const value = context.raw || 0;
                          const total = context.dataset.data.reduce((a, b) => a + b, 0);
                          const percentage = Math.round((value / total) * 100);
                          return `${label}: ${value} (${percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
          
          <div className="chart-card">
            <h3>Points Distribution</h3>
            <div className="chart-container">
              <Bar 
                data={preparePointsData()}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Drivers'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
        
        <div className="chart-row">
          <div className="chart-card">
            <h3>License Expiry Timeline</h3>
            <div className="chart-container">
              <Line 
                data={prepareExpiryData()}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Licenses'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
          
          <div className="chart-card">
            <h3>Check Activity</h3>
            <div className="time-range-selector">
              <button 
                className={timeRange === 'week' ? 'active' : ''} 
                onClick={() => setTimeRange('week')}
              >
                Week
              </button>
              <button 
                className={timeRange === 'month' ? 'active' : ''} 
                onClick={() => setTimeRange('month')}
              >
                Month
              </button>
              <button 
                className={timeRange === 'year' ? 'active' : ''} 
                onClick={() => setTimeRange('year')}
              >
                Year
              </button>
            </div>
            <div className="chart-container">
              <Line 
                data={prepareCheckActivityData()}
                options={{
                  responsive: true,
                  scales: {
                    y: {
                      beginAtZero: true,
                      title: {
                        display: true,
                        text: 'Number of Checks'
                      }
                    }
                  }
                }}
              />
            </div>
          </div>
        </div>
        
        {/* Add a new row for the age distribution chart */}
        <div className="chart-row">
          <div className="chart-card">
            <h3>Driver Age Distribution</h3>
            <div className="chart-container">
              <Pie 
                data={prepareAgeDistributionData()} 
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: 'right',
                    },
                    tooltip: {
                      callbacks: {
                        label: function(context) {
                          const label = context.label || '';
                          const value = context.raw || 0;
                          const ageData = prepareAgeDistributionData();
                          const percentage = ageData.percentages[label];
                          return `${label}: ${value} drivers (${percentage}%)`;
                        }
                      }
                    }
                  }
                }}
              />
            </div>
            <div className="age-category-summary">
              {Object.entries(prepareAgeDistributionData().percentages).map(([category, percentage]) => (
                <div key={category} className="age-category-item">
                  <span className="age-category-label">{category}:</span>
                  <span className="age-category-value">
                    {prepareAgeDistributionData().datasets[0].data[
                      prepareAgeDistributionData().labels.indexOf(category)
                    ]} drivers
                  </span>
                  <span className="age-category-percentage">({percentage}%)</span>
                </div>
              ))}
            </div>
          </div>
          
          {/* You can add another chart here if needed, or leave this as a single chart row */}
        </div>
      </div>
    </div>
  );
}

export default AnalyticsDashboard; 